import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, Modal } from "@mui/material";
import { Close } from "@mui/icons-material";
import Dropzone from "react-dropzone";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import api from "services/api";
import { useAlert } from "contexts/alert";
import layoutCargaOperadora from "assets/documents/layout_carga_operadora.xlsx?url";

import { IAdesaoResult, IUploadIssue, IUploadResult } from "./CargaOperadora.types";
import { UploadContainer } from "./CargaOperadora.styled";
import { CARGA_OPERADORA_QUERY_KEY } from "./CargaOperadora";

type CargaOperadoraUploadModalProps = {
  organizationId: string;
  isOpen: boolean;
  onClose: () => void;
};

const buttonStyle = {
  minWidth: "180px",
  paddingTop: "0.25rem",
  paddingBottom: "0.25rem",
  paddingLeft: "1rem",
  paddingRight: "1rem",
  borderRadius: "0.375rem",
  height: "2.5rem",
  fontSize: "0.875rem",
  lineHeight: "1.25rem",
  fontWeight: 600,
} as const;

/** `YYYY-MM-DD` para `DD/MM/AAAA` sem passar por Date, que deslocaria o dia pelo fuso. */
const formatIsoDate = (iso: string) => iso.split("-").reverse().join("/");

function motivoAdesao(motivo: string, quantos?: number) {
  switch (motivo) {
    case "NAO_E_LIVRE_ADESAO":
      return "o contrato não é de livre adesão";
    case "EM_OPERACAO":
      return "o contrato já está em operação";
    case "AUTOMATICA_VERDADEIRA":
      return quantos ? `${quantos} titulares já têm adesão automática` : "há titulares com adesão automática";
    default:
      return "motivo não informado";
  }
}

/** Uma frase por contrato; vazio quando o usuário não pediu adesão. */
function textoAdesao(a: IAdesaoResult, contrato?: string) {
  const prefixo = contrato ? `Contrato ${contrato}: ` : "";
  if (a.status === "REGISTRADO") {
    return (
      `${prefixo}Aderiram: ${a.titulares} titulares e ${a.dependentes} dependentes. ` +
      `Na posição e fora da massa elegível: ${a.foraDaMassa}. ` +
      `Corte de adesão registrado em ${formatIsoDate(a.cutoffDate)}.`
    );
  }
  if (a.status === "FALHOU") return `${prefixo}adesão e corte não registrados.`;
  if (a.motivo === "OPCAO_DESMARCADA") return "";
  const texto = `corte de adesão não registrado, ${motivoAdesao(a.motivo, a.quantos)}.`;
  return prefixo ? `${prefixo}${texto}` : texto[0].toUpperCase() + texto.slice(1);
}

export function CargaOperadoraUploadModal({
  organizationId,
  isOpen,
  onClose,
}: CargaOperadoraUploadModalProps) {
  const { openAlert } = useAlert();
  const queryClient = useQueryClient();

  const [isUploading, setIsUploading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [registrarAdesao, setRegistrarAdesao] = useState(false);
  const [confirmoLivreAdesao, setConfirmoLivreAdesao] = useState(false);
  /** Não-nulo coloca o modal na tela de críticas. */
  const [issues, setIssues] = useState<IUploadIssue[] | null>(null);
  /** Não-nulo coloca o modal no resultado da adesão, que só sai quando o usuário fecha. */
  const [resultado, setResultado] = useState<{ texto: string; severidade: "success" | "warning" } | null>(null);

  /** Volta ao estado zerado: sem arquivo e sem críticas, exigindo novo upload. */
  function resetToUpload() {
    setFile(null);
    setIssues(null);
    setResultado(null);
    setRegistrarAdesao(false);
    setConfirmoLivreAdesao(false);
  }

  function handleClose() {
    resetToUpload();
    onClose();
  }

  function handleDownloadTemplate() {
    const link = document.createElement("a");
    link.href = layoutCargaOperadora;
    link.download = "layout_carga_operadora.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  async function handleUpload() {
    try {
      if (!file) throw new Error("File is mandatory.");

      setIsUploading(true);

      const formData = new FormData();
      formData.append("file", file as Blob);
      formData.append("registrarAdesao", String(registrarAdesao));
      formData.append("confirmoLivreAdesao", String(registrarAdesao && confirmoLivreAdesao));

      const response = await api.post<{ data: IUploadResult }>(
        `/v2/carga-operadora/${organizationId}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const result = response.data.data;

      /**
       * O endpoint responde 200 mesmo rejeitando o arquivo, então sem checar `valido` a tela
       * diria "sucesso" para um upload que não validou nada.
       */
      if (!result.valido) {
        setIssues(result.erros);

        return;
      }

      const soma = (pegar: (g: IUploadResult["gravadas"][number]) => number) =>
        result.gravadas.reduce((total, item) => total + pegar(item), 0);

      const removidas = soma((g) => g.movimentacaoRemovida);

      const frases = result.gravadas.flatMap((g) => {
        if (!g.adesao) return [];
        const contrato = result.beneficiarios.find((b) => b.workspaceId === g.workspaceId)?.NUMERO_CONTRATO;
        const frase = textoAdesao(g.adesao, contrato);
        return frase ? [` ${frase}`] : [];
      });
      const aviso = result.gravadas.some(
        (g) =>
          g.adesao?.status === "FALHOU" ||
          g.adesao?.status === "RECUSADO" ||
          (g.adesao?.status === "REGISTRADO" && g.adesao.foraDaMassa > 0)
      );
      const texto =
        `${result.beneficiarios.length} linhas válidas: ${soma((g) => g.posicaoCadastral)} na posição cadastral e ` +
        `${soma((g) => g.baseline)} como cobertura já ativa.` +
        (removidas ? ` ${removidas} linhas de movimentação anterior foram removidas.` : "") +
        frases.join("");

      /** A tela lê o que está gravado, então invalidar é o que faz o resultado aparecer. */
      await queryClient.invalidateQueries({ queryKey: [CARGA_OPERADORA_QUERY_KEY, organizationId] });

      // why: o resultado da adesão (contagens, data do corte, falha) não pode sumir num alerta de 5 s.
      if (frases.length > 0) {
        setResultado({ texto, severidade: aviso ? "warning" : "success" });
        return;
      }

      openAlert(texto, "success");
      handleClose();
    } catch (error) {
      //@ts-ignore
      if (error?.response?.data?.data?.message) {
        //@ts-ignore
        openAlert(error?.response?.data?.data?.message, "error");
      } else {
        openAlert("Houve algum erro ao enviar o arquivo.", "error");
      }

      /** Erro de API fecha o modal — não há nada que o usuário possa corrigir aqui. */
      handleClose();
    } finally {
      setIsUploading(false);
    }
  }

  return (
    <Modal open={isOpen} aria-labelledby="carga-operadora-modal">
      <Box sx={{ position: "absolute" as "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", height: "auto", width: "100%", maxWidth: 900, bgcolor: "background.paper", boxShadow: 24, borderRadius: "1rem", border: "none", outline: "none", padding: "24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h6 style={{ padding: 0, margin: 0, fontSize: "1.25rem", lineHeight: "1.75rem", fontWeight: 600 }}>
            {issues ? "Críticas encontradas" : "Carregar posição da operadora"}
          </h6>
          <Close style={{ cursor: "pointer" }} onClick={handleClose} />
        </div>

        {resultado ? (
          <>
            <Alert severity={resultado.severidade} style={{ marginTop: "24px" }}>
              {resultado.texto}
            </Alert>

            <div style={{ display: "flex", paddingTop: "0.5rem", paddingBottom: "0.5rem", marginTop: "1rem", justifyContent: "flex-end", alignItems: "center", width: "100%" }}>
              <Button variant="contained" style={buttonStyle} onClick={handleClose}>
                Fechar
              </Button>
            </div>
          </>
        ) : issues ? (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: "16px", marginTop: "24px" }}>
              <span style={{ fontSize: "16px", lineHeight: "175%", fontWeight: 400, color: "#191C1A" }}>
                O arquivo foi rejeitado por completo e nada foi processado. Corrija{" "}
                {issues.length === 1 ? "a crítica" : `as ${issues.length} críticas`} abaixo e envie a
                planilha novamente.
              </span>

              <div style={{ maxHeight: "360px", overflowY: "auto", border: "solid 1px #E8E8E8", borderRadius: "16px" }}>
                {issues.map((issue, index) => (
                  <div
                    key={`${issue.linha}-${issue.campo}-${index}`}
                    style={{ display: "flex", gap: "16px", padding: "12px 16px", borderBottom: index === issues.length - 1 ? "none" : "solid 1px #F2F2F2" }}
                  >
                    <span style={{ fontSize: "13px", fontWeight: 600, color: "#191C1A", minWidth: "70px" }}>
                      Linha {issue.linha}
                    </span>
                    <span style={{ fontSize: "13px", fontWeight: 600, color: "#6F6F78", minWidth: "220px" }}>
                      {issue.campo}
                    </span>
                    <span style={{ fontSize: "13px", color: "#6F6F78" }}>{issue.mensagem}</span>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: "flex", paddingTop: "0.5rem", paddingBottom: "0.5rem", marginTop: "1rem", justifyContent: "flex-end", alignItems: "center", width: "100%" }}>
              <Button variant="contained" style={buttonStyle} onClick={resetToUpload}>
                Voltar
              </Button>
            </div>
          </>
        ) : (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: "24px", marginTop: "24px" }}>
              <span style={{ fontSize: "16px", lineHeight: "175%", fontWeight: 400, color: "#191C1A" }}>
                Faça upload de um arquivo Excel (
                <span
                  onClick={handleDownloadTemplate}
                  style={{ color: "#1976d2", cursor: "pointer", textDecoration: "underline", fontWeight: 500 }}
                >
                  use este template
                </span>
                ) com a posição que a operadora informa. Envie <strong>um arquivo por empresa
                compradora</strong>: se o export da operadora tiver mais de um CNPJ de
                subestipulante, separe antes. O template traz uma aba com o guia de preenchimento de
                cada coluna.
              </span>

              {/**
                * Aviso obrigatório: a carga esvazia a movimentação do contrato. É irreversível
                * pelo caminho normal, então o usuário precisa saber antes de anexar o arquivo.
                */}
              <div style={{ border: "solid 1px #E8A33D", background: "#FFF8E6", borderRadius: "12px", padding: "12px 16px", display: "flex", gap: "12px", alignItems: "flex-start" }}>
                <WarningAmberIcon style={{ color: "#B26B00", fontSize: "20px", marginTop: "2px" }} />
                <span style={{ fontSize: "13px", lineHeight: "160%", color: "#5C3D00" }}>
                  <strong>A carga apaga a movimentação atual deste contrato.</strong> Toda linha da
                  massa movimentada é removida e substituída pela posição da operadora — inclusive
                  movimentações pendentes de aprovação. A posição cadastral e os dados do eSocial
                  não são afetados. Só acontece se o arquivo for aceito por completo.
                </span>
              </div>

              <div style={{ display: "flex", flexDirection: "column" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={registrarAdesao}
                      onChange={(e) => {
                        setRegistrarAdesao(e.target.checked);
                        if (!e.target.checked) setConfirmoLivreAdesao(false);
                      }}
                    />
                  }
                  label="Contrato de livre adesão: registrar quem aderiu e descartar quem não está na posição"
                />
                {registrarAdesao && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={confirmoLivreAdesao}
                        onChange={(e) => setConfirmoLivreAdesao(e.target.checked)}
                      />
                    }
                    label="Confirmo que este contrato é de livre adesão: o colaborador só entra se pedir"
                  />
                )}
              </div>

              {!!file && (
                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "8px" }}>
                  <InsertDriveFileOutlinedIcon style={{ width: "24px" }} />
                  <span style={{ fontSize: "13px", fontWeight: 600, color: "#191C1A" }}>{file.name}</span>
                  <DeleteIcon color="error" style={{ width: "24px", cursor: "pointer" }} onClick={() => setFile(null)} />
                </div>
              )}

              {!file && (
                <Dropzone
                  onDrop={(acceptedFiles) => (acceptedFiles.length ? setFile(acceptedFiles[0]) : setFile(null))}
                  accept={{
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
                  }}
                >
                  {({ getRootProps, getInputProps, isDragActive }) => (
                    <div className="container" style={{ marginTop: "-24px" }}>
                      <UploadContainer {...getRootProps({ className: "dropzone" })}>
                        <input {...getInputProps()} />
                        <FileUploadIcon style={{ fontSize: "24px", color: "#3024E1" }} />
                        {isDragActive ? (
                          <>
                            <p style={{ color: "#3024E1", fontSize: "16px", margin: 0 }}>Solte o arquivo aqui…</p>
                            <p style={{ fontSize: "12px", margin: 0, color: "#0F0F1E99" }}>Liberando para upload</p>
                          </>
                        ) : (
                          <>
                            <p style={{ color: "#3024E1", fontSize: "16px", margin: 0 }}>Arraste e solte ou clique para procurar</p>
                            <p style={{ fontSize: "12px", margin: 0, color: "#0F0F1E99" }}>Somente arquivos no formato xlsx são aceitos</p>
                          </>
                        )}
                      </UploadContainer>
                    </div>
                  )}
                </Dropzone>
              )}
            </div>

            <div style={{ display: "flex", paddingTop: "0.5rem", paddingBottom: "0.5rem", marginTop: "1rem", justifyContent: "flex-end", alignItems: "center", width: "100%" }}>
              <div style={{ display: "flex", gap: "0.25rem" }}>
                <Button variant="outlined" color="error" style={buttonStyle} onClick={handleClose} disabled={isUploading}>
                  Cancelar
                </Button>

                <Button variant="contained" style={buttonStyle} onClick={handleUpload} disabled={isUploading || !file || (registrarAdesao && !confirmoLivreAdesao)}>
                  {!isUploading ? "Conferir" : <CircularProgress size="20px" />}
                </Button>
              </div>
            </div>
          </>
        )}
      </Box>
    </Modal>
  );
}
